import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { TriggerRow } from './TriggerRow';
import {
  TYPE_LABEL,
  TRIGGER_LIST_GRID_COLUMNS,
  getTagLabel,
  getAgentLabel,
  getUserIdLabel,
} from './triggerListHelpers';

const ListWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${TRIGGER_LIST_GRID_COLUMNS};
  gap: 12px;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }
`;

const HeaderRow = styled(Row)`
  background: ${({ theme }) => theme.themeColors.appBackground};
  font-weight: 600;
`;

const HeaderSelect = styled.select`
  background: transparent;
  border: none;
  font-size: inherit;
  font-weight: inherit;
  color: ${({ theme }) => theme.themeColors.text};
  cursor: pointer;
  padding: 0;
  margin: 0;
  width: 100%;
  min-width: 0;

  &:focus {
    outline: none;
  }
`;

export function TriggerList({ triggers, onDeleted }) {
  const [filterType, setFilterType] = React.useState('');
  const [filterTag, setFilterTag] = React.useState('');
  const [filterAgent, setFilterAgent] = React.useState('');
  const [filterUserId, setFilterUserId] = React.useState('');

  const { typeOptions, tagOptions, agentOptions, userIdOptions } = React.useMemo(() => {
    if (!triggers?.length) {
      return { typeOptions: [], tagOptions: [], agentOptions: [], userIdOptions: [] };
    }
    const types = [
      ...new Set(triggers.map((t) => TYPE_LABEL[t.trigger_type] || t.trigger_type).filter(Boolean)),
    ].sort();
    const tags = [...new Set(triggers.map(getTagLabel).filter(Boolean))].sort();
    const agents = [...new Set(triggers.map(getAgentLabel).filter(Boolean))].sort();
    const userIds = [...new Set(triggers.map(getUserIdLabel).filter((v) => v !== '—'))].sort(
      (a, b) => Number(a) - Number(b)
    );
    return {
      typeOptions: types,
      tagOptions: tags,
      agentOptions: agents,
      userIdOptions: userIds,
    };
  }, [triggers]);

  const filteredTriggers = React.useMemo(() => {
    if (!triggers) return [];
    return triggers.filter((trigger) => {
      const typeLabel = TYPE_LABEL[trigger.trigger_type] || trigger.trigger_type;
      const tagLabel = getTagLabel(trigger);
      const agentLabel = getAgentLabel(trigger);
      const userIdLabel = getUserIdLabel(trigger);
      if (filterType && typeLabel !== filterType) return false;
      if (filterTag && tagLabel !== filterTag) return false;
      if (filterAgent && agentLabel !== filterAgent) return false;
      if (filterUserId && userIdLabel !== filterUserId) return false;
      return true;
    });
  }, [triggers, filterType, filterTag, filterAgent, filterUserId]);

  if (!triggers || triggers.length === 0) {
    return (
      <Container top={16} bottom={48}>
        <Body5 isLowContrast>No triggers. Create one below.</Body5>
      </Container>
    );
  }

  return (
    <ListWrapper>
      <HeaderRow>
        <div>
          <HeaderSelect value={filterType} onChange={(e) => setFilterType(e.target.value)} aria-label="Filter by type">
            <option value="">Type</option>
            {typeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </HeaderSelect>
        </div>
        <div>
          <HeaderSelect value={filterTag} onChange={(e) => setFilterTag(e.target.value)} aria-label="Filter by tag">
            <option value="">Tag</option>
            {tagOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </HeaderSelect>
        </div>
        <div>
          <HeaderSelect
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            aria-label="Filter by user ID"
          >
            <option value="">User ID</option>
            {userIdOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </HeaderSelect>
        </div>
        <div>
          <HeaderSelect
            value={filterAgent}
            onChange={(e) => setFilterAgent(e.target.value)}
            aria-label="Filter by agent"
          >
            <option value="">Agent</option>
            {agentOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </HeaderSelect>
        </div>
        <div>
          <Body5 bold>Concurrency</Body5>
        </div>
        <div>
          <Body5 bold>Prompt</Body5>
        </div>
        <div>
          <Body5 bold>Created</Body5>
        </div>
        <div />
      </HeaderRow>
      {filteredTriggers.map((trigger) => (
        <TriggerRow key={trigger.id} trigger={trigger} onDelete={onDeleted} />
      ))}
    </ListWrapper>
  );
}
